import React, {useState, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {Controller} from 'react-hook-form';
import {useSelector} from 'react-redux';

import {fetchSelectData, getErrors} from '../../../utils';
import SelectInput from '../../../components/ui/Inputs/SelectInput';
import TextInput from '../../../components/ui/Inputs/TextInput';
import TextAreaInput from '../../../components/ui/Inputs/TextAreaInput';
import Button from '../../../components/ui/Button';
import FileDragNDropInput from "../../../components/ui/Inputs/FileDragNDropInput"
import {notify} from '../../../components/ui/Toast/Toast';
import {
    DealActions,
    ContractorActions,
    FileActions,
    ContractorInvoiceActions,
    InvoiceActions
} from '../../../actions';
import Error from '../../../components/ui/Error';

function SupplierInvoiceForm({
    setView,
    invoiceContractorToBeUpdated,
    refreshData
}) {
    // -- states --
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState(null);
    const [deals, setDeals] = useState(null);
    const [invoices, setInvoices] = useState(null);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [contractors, setContractors] = useState(null);
    const [contractorsLoading, setContractorsLoading] = useState(false);

    const userData = useSelector((state) => state.UserReducer.userProfileData);
    const vat = useSelector((state) => state.GlobalsReducer.vat);

    // -- react hook form --
    const defaultValues = {
        deal: null,
        invoice: null,
        contractor: null,
        amount: null,
        notes: "",
        gross_amount: null,
        recoverable_vat: vat,
        file: null
    };

    const {handleSubmit, control, reset, watch, setValue, formState: {isValid}} = useForm({defaultValues});

    const dealValue = watch('deal');

    // -- fill invoiceContractor values in form for update --
    useEffect(() => {

        if (invoiceContractorToBeUpdated && invoiceContractorToBeUpdated.id) {
            reset({
                deal: invoiceContractorToBeUpdated.invoice.deal.id,
                invoice: invoiceContractorToBeUpdated.invoice,
                contractor: invoiceContractorToBeUpdated.contractor,
                amount: (+invoiceContractorToBeUpdated.amount).toString(),
                recoverable_vat: (+invoiceContractorToBeUpdated.recoverable_vat).toString(),
                gross_amount: (+invoiceContractorToBeUpdated.gross_amount).toString(),
                file: invoiceContractorToBeUpdated.file,
                notes: invoiceContractorToBeUpdated.notes
            });
        }

    }, [invoiceContractorToBeUpdated]);

    // -- functions that get data for Select Inputs --
    const handleGetDeals = async (search, page = 1) => {
        await fetchSelectData({
            action: DealActions.getAllDeals,
            search:`&type=business&display=${userData.userProfileName}&search=${search}`,
            page: page,
            setter: setDeals,
            userData,
            resultPath: 'data'
        });
    };

    const handleGetInvoices = async (deal) => {
        if (dealValue) {
            setInvoicesLoading(true);

            const result = await InvoiceActions.getAllInvoices(userData, `&deal=${deal.id}&page_size=2000`, 1);

            setInvoices(result.data);

            setInvoicesLoading(false);
        }
    };

    const handleGetContractors = async (deal) => {
        if (dealValue) {
            setContractorsLoading(true);

            const result = await ContractorActions.getAllContractors(userData, `&deal=${deal.id}`, 1);

            setContractors(result.data);

            setContractorsLoading(false);
        }
    };

    useEffect(() => {
        if (dealValue) {
            setValue('invoice', null);
            setValue('contractor', null);

            handleGetInvoices(dealValue);
            handleGetContractors(dealValue);
        }
    }, [dealValue])

    useEffect(() => {
        // ↓ contractors dropdown values
        (async () => {
            await handleGetContractors('', 1);
        })();
        (async () => {
            await handleGetInvoices('', 1);
        })();

    }, []);

    // -- handle submit --
    const onSubmit = async (data) => {
        setDisabled(true);
        setLoading(true);
        setErrors(null);

        let file = null;

        if (data.file && data.file.uid && data.file.type) {
            const newFile = {
                "type": data.file.type,
                "name": data.file.name,
                "file": data.file
            }

            const result = await FileActions.createFile(userData, newFile);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                file = result.data;
            }
        }

        // ↓ body to send to the post/patch request (see API documentation)
        const contractor = {
            invoice_id: data.invoice?.id,
            contractor_id: data.contractor?.id,
            amount: data.amount,
            gross_amount: data.gross_amount,
            recoverable_vat: data.recoverable_vat,
            notes: data.notes ? data.notes : ""
        };

        if (file) {
            contractor.file_id = file.id;
        }

        // ↓ post - new invoiceContractor
        if (!invoiceContractorToBeUpdated) {
            const result = await ContractorInvoiceActions.createContractorInvoice(userData, contractor);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                await refreshData();
                notify('La facture sous/co-traitant à bien été crée');

                setView(false);
                reset();
            }
        }
        // ↓ patch - update invoiceContractor
        else {

            const result = await ContractorInvoiceActions.updateContractorInvoice(userData, contractor, invoiceContractorToBeUpdated.id);

            if (!result.success) {
                setErrors(getErrors(result));
            }
            else {
                await refreshData();
                notify('La facture sous/co-traitant à bien été modifié');

                setView(false);
                reset();
            }
        }

        setLoading(false);
        setDisabled(false);
    };

    return (
        <div>
            <div className="pb-4 shadow-custom-outer">
                <div className="py-4 pl-4">
                    <h3 className="text-xl font-bold title">
                        {
                            (invoiceContractorToBeUpdated && invoiceContractorToBeUpdated.id) ?
                                'Éditer la facture fournisseur'
                                :
                                'Nouvelle facture fournisseur'
                        }
                    </h3>
                </div>
                <div className="flex flex-row justify-between py-6 pl-4 pr-4 shadow-custom-inner">
                    <div>
                        <div className="mb-2">
                            <Controller
                                name="deal"
                                control={control}
                                rules={{required: {
                                    value: true,
                                    message: 'Veuillez sélectionner une affaire.'
                                }}}
                                render={({
                                    field: {onChange, value, ref},
                                    fieldState: {error}
                                }) => (
                                    <SelectInput
                                        label='Affaire'
                                        placeholder='Affaire'
                                        inputRef={ref}
                                        value={value}
                                        error={error}
                                        options={deals?.options || []}
                                        hasNextPage={deals?.hasNextPage || false}
                                        fetchFunction={handleGetDeals}
                                        loading={!deals}
                                        onChange={onChange}
                                        labelKeys={['short_name']}
                                        isSearchable={true}
                                        required={true}
                                        disabled={disabled}
                                    />
                                )}
                            />
                        </div>
                        <div className="flex mb-2">
                            <Controller
                                name="invoice"
                                control={control}
                                rules={{required: {
                                    value: true,
                                    message: 'Veuillez sélectionner une facture.'
                                }}}
                                render={({
                                    field: {onChange, value, ref},
                                    fieldState: {error}
                                }) => (
                                    <SelectInput
                                        label='Facture'
                                        placeholder="Facture Terre d'Avance"
                                        inputRef={ref}
                                        value={value}
                                        error={error}
                                        options={invoices}
                                        loading={invoicesLoading}
                                        onChange={onChange}
                                        isSearchable={true}
                                        labelKeys={["deal.short_name", "billing_date"]}
                                        separator=' - '
                                        required={true}
                                        disabled={!invoices || disabled}
                                    />
                                )}
                            />
                        </div>
                        <div className="flex mb-2">
                            <Controller
                                name="contractor"
                                control={control}
                                rules={{required: {
                                    value: true,
                                    message: 'Veuillez sélectionner un fournisseur.'
                                }}}
                                render={({
                                    field: {onChange, value, ref},
                                    fieldState: {error}
                                }) => (
                                    <SelectInput
                                        label='Fournisseur'
                                        placeholder='Fournisseur'
                                        inputRef={ref}
                                        value={value}
                                        error={error}
                                        options={contractors}
                                        loading={contractorsLoading}
                                        onChange={onChange}
                                        isSearchable={true}
                                        labelKeys={['name']}
                                        required={true}
                                        disabled={!contractors || disabled}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col place-content-end">
                        <div className="flex mb-2">
                            <Controller
                                name="amount"
                                control={control}
                                render={({
                                    field: {onChange, value},
                                    fieldState: {error}
                                }) => (
                                    <TextInput
                                        label={'Montant TTC (€)'}
                                        onChange={onChange}
                                        value={value}
                                        error={error}
                                        disabled={disabled}
                                        placeholder="Montant TTC"
                                        type="number"
                                    />
                                )}
                            />
                        </div>
                        <div className="flex mb-2">
                            <Controller
                                name="recoverable_vat"
                                control={control}
                                render={({
                                    field: {onChange, value},
                                    fieldState: {error}
                                }) => (
                                    <TextInput
                                        label={'TVA recupérable'}
                                        onChange={onChange}
                                        value={value}
                                        error={error}
                                        disabled={disabled}
                                        placeholder="TVA recupérable"
                                        type="number"
                                    />
                                )}
                            />
                        </div>

                        <div className="flex mb-2">
                            <Controller
                                name="gross_amount"
                                control={control}
                                render={({
                                    field: {onChange, value},
                                    fieldState: {error}
                                }) => (
                                    <TextInput
                                        label={'Montant HT (€)'}
                                        onChange={onChange}
                                        value={value}
                                        error={error}
                                        disabled={disabled}
                                        placeholder="Montant HT"
                                        type="number"
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>

                <div className="px-4 mb-5">
                    <Controller
                        name="file"
                        control={control}
                        render={({
                            field: {onChange}
                        }) => (
                            <FileDragNDropInput
                                onChange={onChange}
                                uploadText='Glissez déposez ou séllectionnez un fichiers'
                                uploadHint="Ajoutez ici des documents (PDF, JPEG, PDF)"
                                maxCount={1}
                            />
                        )}
                    />
                </div>

                <div className="px-4">
                    <div className="py-4 border-t border-borderGrey">
                        <Controller
                            name="notes"
                            control={control}
                            render={({
                                field: {onChange, value}
                            }) => (
                                <TextAreaInput
                                    placeholder={'Tapez votre texte'}
                                    maxLength={100}
                                    onChange={onChange}
                                    value={value}
                                    disabled={disabled}
                                />
                            )}
                        />
                    </div>
                </div>

                <div className="flex flex-col justify-center mt-6 mb-4">
                    <div className="flex justify-center">
                        <Button
                            onClick={() => {
                                reset();
                                setView(false);
                            }}
                            type={'secondary'}
                            content={'Annuler'}
                            disabled={disabled}
                        />
                        {
                            (!invoiceContractorToBeUpdated) ?
                                <Button
                                    type='primary'
                                    content={'Ajouter la facture fournisseur'}
                                    onClick={handleSubmit(onSubmit)}
                                    loading={loading}
                                    disabled={!isValid}
                                />
                                : null
                        }
                        {
                            (invoiceContractorToBeUpdated && invoiceContractorToBeUpdated.id) ?
                                <Button
                                    type='primary'
                                    content={'Modifier la facture fournisseur'}
                                    onClick={handleSubmit(onSubmit)}
                                    loading={loading}
                                    disabled={(!contractors) ? true : !isValid}
                                />
                                : null
                        }
                    </div>
                    <Error errors={errors}/>
                </div>
            </div>
        </div>
    );
}

export default SupplierInvoiceForm;
